import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, BookButtons } from "../components/sections";
import { IMAGES } from "../data/site";

const AUDIENCE_DATA = {
  women: {
    eyebrow: "Women Driving Confidence",
    lines: ["Confidence While Driving", "Starts With The Right Training."],
    heroText: "Confidence-focused driving training for women who feel nervous or hesitant while driving — patient trainers, real-road practice and simple psychology-based methods that respect your pace.",
    image: IMAGES.hero,
    imageAlt: "Confident Indian woman learner driver during DWC training",
    problemTitle: "We Understand Why Driving Feels Difficult",
    problems: ["Fear of traffic and being judged by other drivers", "Fear of making mistakes or damaging the car", "Parking anxiety in public places", "Fear of highway driving", "Depending on family members to drive", "Long gap without practice after getting a licence"],
    approachTitle: "How DWC Trains Women Differently",
    approach: ["Patient, professional trainers who understand nervous learners", "Step-by-step real-road practice — quiet roads before busy ones", "Simple methods to handle fear, hesitation and overthinking", "Parking, reversing and highway practice included", "Learn in your own car, where applicable", "A written money-back driving guarantee on qualifying programs"],
    finalTitle: "Your Independence Starts With Driving.",
    finalSub: "Join the many women across Mumbai, Surat & Ahmedabad who learned to drive confidently with DWC.",
  },
  senior: {
    eyebrow: "Senior Citizen Driving",
    lines: ["It's Never Too Late", "To Drive With Confidence."],
    heroText: "A patient, personalised driving program for senior citizens — comfortable pace, modern car familiarity and complete family reassurance.",
    image: IMAGES.senior,
    imageAlt: "Senior citizen learning to drive confidently with DWC",
    problemTitle: "Designed Around Comfort, Not Speed",
    problems: ["Patient training with no pressure and no rushing", "A comfortable learning pace set entirely by you", "Familiarity with modern car controls and features", "Road awareness and defensive driving for today's traffic", "Parking and reversing practice for daily independence", "Regular progress updates for your family"],
    approachTitle: "What The Program Focuses On",
    approach: ["Car controls and comfortable driving posture", "Slow-speed control before any traffic", "Real-road awareness at a pace that suits you", "Parking, U-turns and everyday driving practice", "Confidence for independent errands and family visits", "No age limit — fitness and willingness are what matter"],
    finalTitle: "Independence Has No Age Limit.",
    finalSub: "DWC's senior citizen program has helped learners of all ages drive with comfort and confidence.",
  },
  advanced: {
    eyebrow: "Advanced & Defensive Driving",
    lines: ["Already Drive?", "Now Drive Exceptionally Well."],
    heroText: "Advanced road skills and defensive driving training for licence holders who want to improve — highways, night driving, difficult situations and anticipation-first awareness.",
    image: IMAGES.streetView,
    imageAlt: "View from inside a car on a busy Indian road — advanced driving training context",
    problemTitle: "What Advanced Training Covers",
    problems: ["Highway driving with confidence at speed", "Night driving and low-visibility awareness", "Defensive driving — understanding other road users", "Difficult situations: steep roads, heavy congestion, rain", "Emergency awareness and basic car care", "Advanced driving practice and precise car control"],
    approachTitle: "The Defensive Driving Mindset",
    approach: ["Anticipation: seeing risks before they develop", "Safe distance and space management", "Calm decisions at highway speeds", "Awareness beyond your own lane", "Responsible, patient behaviour under pressure", "Habits that keep you — and others — safer for life"],
    finalTitle: "Good Drivers Pass Tests. Great Drivers Stay Prepared.",
    finalSub: "Take your driving from adequate to excellent with DWC's advanced and defensive training.",
  },
};

const Audience = ({ kind }) => {
  const d = AUDIENCE_DATA[kind];
  return (
    <>
      <PageHero eyebrow={d.eyebrow} lines={d.lines} text={d.heroText} image={d.image} imageAlt={d.imageAlt}>
        <BookButtons bookTestId={`${kind}-book-btn`} waTestId={`${kind}-whatsapp-btn`} />
      </PageHero>
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="The Reality" number="01" title={d.problemTitle} />
            <ul className="mt-10 space-y-3">
              {d.problems.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-sm font-semibold text-dwc-charcoal/80 shadow-[0_4px_16px_rgb(0,0,0,0.03)]">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-crimson" aria-hidden="true" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="The DWC Approach" number="02" title={d.approachTitle} />
            <ul className="mt-10 space-y-3">
              {d.approach.map((a) => (
                <li key={a} className="flex items-start gap-3 rounded-xl border border-dwc-green/15 bg-dwc-soft p-4 text-sm font-semibold text-dwc-charcoal/80">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <CTASection title={d.finalTitle} sub={d.finalSub} chapter="03" />
    </>
  );
};

export default Audience;
